import { useEffect, useMemo, useRef, useState, createContext, useContext } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { ArrowUpRight, Menu, X, Instagram, Mail, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/lib/content";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { rich, richBlocks } from "@/lib/richtext";
import ThemeToggle from "@/components/ThemeToggle";

const LOGO = "/logo.webp";
const LOGO_LIGHT = "/logo-white.webp"; // weißes Logo für dunkle Flächen (Menüband)

// Lucide hat kein TikTok-Icon – kleines eigenes Inline-SVG (Standard-„Note"-Glyphe).
function TikTokIcon({ size = 18, className = "" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

const ContentCtx = createContext(DEFAULT_CONTENT);
const useC = () => useContext(ContentCtx);

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { rootMargin: "-60px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ transitionDelay: `${delay}s` }} className={`reveal ${shown ? "reveal-in" : ""} ${className}`}>
      {children}
    </div>
  );
}

function MaskLine({ children, delay = 0 }) {
  return <span className="block anim-fade-up" style={{ animationDelay: `${delay}s` }}>{children}</span>;
}

function buildNav(hasMarkets) {
  const nav = [
    { label: "Werke", href: "#galerie" },
    { label: "Über mich", href: "#ueber-mich" },
    { label: "Handwerk", href: "#handwerk" },
    { label: "Workshops", href: "#workshops" },
  ];
  if (hasMarkets) nav.push({ label: "Märkte", href: "#maerkte" });
  nav.push({ label: "Kontakt", href: "#kontakt" });
  return nav;
}

function Header({ hasMarkets }) {
  const [open, setOpen] = useState(false);
  const nav = buildNav(hasMarkets);
  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="mt-4 flex items-center justify-between rounded-full border border-sage/25 bg-forest-deep/85 backdrop-blur-md pl-2.5 pr-5 py-1.5">
          <a href="#top" data-testid="logo-link" className="logo-link flex items-center gap-3">
            <img src={LOGO_LIGHT} onError={(e) => { e.currentTarget.src = LOGO; }} alt="Tonpoesie Keramik Studio" className="logo-spin h-24 w-24 -my-6 rounded-full object-cover" />
            <span className="font-serif text-3xl leading-none tracking-tight text-bone">Tonpoesie</span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            {nav.map((n) => (
              <a key={n.href} href={n.href} data-testid={`nav-${n.label}`}
                 className="nav-link text-sm text-bone/70 hover:text-bone transition-colors">
                {n.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle className="hidden sm:inline-flex p-2 text-bone/70 hover:text-bone" />
            <a href="#kontakt" data-testid="header-cta"
               className="btn-lift hidden sm:inline-flex items-center gap-1.5 rounded-full bg-sage px-5 py-2 text-sm font-medium text-forest-deep hover:bg-bone transition-colors">
              Anfragen <ArrowUpRight size={15} />
            </a>
            <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="md:hidden p-2 text-bone">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden mt-2 rounded-3xl border border-sage/25 bg-forest-deep p-4 flex flex-col gap-1">
            {nav.map((n) => (
              <a key={n.href} href={n.href} onClick={() => setOpen(false)}
                 className="px-3 py-2 text-bone hover:bg-white/10 rounded-xl transition-colors">{n.label}</a>
            ))}
            <div className="flex items-center justify-between px-3 py-2">
              <span className="text-sm text-bone/70">Farbschema</span>
              <ThemeToggle className="p-2 text-bone/70 hover:text-bone hover:bg-white/10" />
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function Hero() {
  const c = useC();
  return (
    <section id="top" className="relative min-h-screen overflow-hidden bg-olive-ink">
      <div className="absolute inset-0">
        <img
          src={c.hero_image}
          alt="Töpfern an der Scheibe"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-forest-deep/45 to-forest-deep/65" />
        <div className="absolute inset-0 bg-sage/30 mix-blend-multiply" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-6 md:px-10 pb-16 md:pb-24">
        <p className="anim-fade-up mb-6 text-xs uppercase tracking-[0.28em] text-sage" style={{ animationDelay: "0.1s" }}>
          {c.hero_overline}
        </p>
        <h1 className="font-serif text-bone tracking-tighter leading-[0.86] text-[15vw] sm:text-[12vw] md:text-[8.5vw]">
          <MaskLine delay={0.2}>{c.hero_line1}</MaskLine>
          <MaskLine delay={0.34}><span className="italic text-sage">{c.hero_line2_accent}</span>{c.hero_line2_suffix}</MaskLine>
          <MaskLine delay={0.48}>{c.hero_line3}</MaskLine>
        </h1>
        <div className="anim-fade-up mt-10 flex flex-col sm:flex-row sm:items-end gap-6 sm:justify-between" style={{ animationDelay: "0.72s" }}>
          <p className="max-w-md text-bone/70 leading-relaxed">
            {rich(c.hero_subtitle)}
          </p>
          <div className="flex gap-3">
            <a href="#galerie" data-testid="hero-gallery-btn"
               className="btn-lift inline-flex items-center gap-1.5 rounded-full bg-sage px-6 py-3 text-sm font-medium text-sage-deep hover:bg-sage-dark transition-colors">
              Werke ansehen <ArrowUpRight size={16} />
            </a>
            <a href="#workshops" data-testid="hero-workshop-btn"
               className="btn-lift inline-flex items-center rounded-full border border-bone/30 px-6 py-3 text-sm font-medium text-bone hover:bg-bone/10 transition-colors">
              Workshops
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function About() {
  const c = useC();
  return (
    <section id="ueber-mich" className="bg-panel2 py-24 md:py-36 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid md:grid-cols-12 gap-12 md:gap-16 items-center">
        <Reveal className="md:col-span-5">
          <div className="relative">
            <div className="overflow-hidden rounded-t-[10rem] rounded-b-[1.5rem] shadow-[0_30px_60px_-25px_rgba(0,0,0,0.25)]">
              <img
                src={c.about_image}
                alt="Die Töpferin"
                className="w-full h-[30rem] md:h-[36rem] object-cover"
              />
            </div>
            {(c.about_badge_number || "").trim() || (c.about_badge_label || "").trim() ? (
              <div className="float-slow absolute -bottom-6 -left-4 md:-left-8 bg-bone border border-line rounded-2xl px-6 py-4 shadow-lg">
                <p className="font-serif text-4xl text-sage-deep leading-none">{c.about_badge_number}</p>
                <p className="text-xs uppercase tracking-widest text-olive-muted mt-1 max-w-[7rem]">{c.about_badge_label}</p>
              </div>
            ) : null}
          </div>
        </Reveal>

        <div className="md:col-span-7 md:pl-8">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] text-sage mb-4">{c.about_overline}</p>
            <h2 className="font-serif text-ink text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02]">
              {c.about_heading} <span className="italic text-sage">{c.about_name}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 space-y-5 text-inkmuted leading-relaxed max-w-xl">
              {[c.about_p1, c.about_p2, c.about_p3].filter(Boolean).map((p, i) => <p key={i}>{rich(p)}</p>)}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap gap-x-12 gap-y-6">
              {(c.about_stats || []).map((s, i) => (
                <div key={i}>
                  <p className="font-serif text-3xl text-sage-deep">{s.value}</p>
                  <p className="text-sm text-inkmuted mt-1">{s.label}</p>
                </div>
              ))}
            </div>
            <a href="#kontakt" data-testid="about-contact-btn"
               className="btn-lift mt-10 inline-flex items-center gap-1.5 rounded-full bg-sage px-6 py-3 text-sm font-medium text-sage-deep hover:bg-sage-dark transition-colors">
              Sag Hallo <ArrowUpRight size={16} />
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const c = useC();
  const words = (c.marquee_items || "").split("·").map((w) => w.trim()).filter(Boolean);
  const items = [];
  words.forEach((w) => { items.push(w); items.push("·"); });
  return (
    <div className="border-y border-forest/40 bg-forest-deep py-6 overflow-hidden">
      <div className="flex w-max animate-marquee">
        {[0, 1].map((k) => (
          <div key={k} className="flex items-center gap-10 pr-10" aria-hidden={k === 1}>
            {items.map((w, i) => (
              <span key={i} className={`font-serif text-3xl md:text-4xl ${w === "·" ? "text-sage" : "text-bone"}`}>{w}</span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Manifesto() {
  const c = useC();
  return (
    <section id="handwerk" className="relative bg-page py-28 md:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.28em] text-sage mb-4">{c.handwerk_overline}</p>
          <h2 className="font-serif text-ink text-4xl sm:text-5xl md:text-6xl tracking-tight max-w-3xl leading-[1.05]">
            {c.handwerk_heading}
          </h2>
        </Reveal>
        <div className="mt-20 grid md:grid-cols-3 gap-12 md:gap-8">
          {(c.chapters || []).map((ch, i) => (
            <Reveal key={i} delay={i * 0.12} className="relative">
              <span className="font-serif text-7xl md:text-8xl text-sage/25 leading-none">{ch.n}</span>
              <h3 className="font-serif text-2xl md:text-3xl text-ink mt-2">{ch.t}</h3>
              <p className="mt-4 text-inkmuted leading-relaxed max-w-xs">{rich(ch.d)}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function GalleryCard({ p, i, onInquire }) {
  const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-6", "md:col-span-6"];
  const unavailable = p.available === false;
  return (
    <Reveal delay={(i % 2) * 0.1} className={`${spans[i % spans.length]} group`}>
      <div className="hover-lift relative overflow-hidden rounded-[1.5rem] bg-panel2 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.15)]">
        <div className="aspect-[4/5] overflow-hidden">
          <img src={p.image_url} alt={p.title} style={{ objectPosition: p.image_pos || "50% 50%" }}
               className={`h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110 ${unavailable ? "opacity-80 grayscale-[0.3]" : ""}`} />
        </div>
        <div className="absolute top-4 left-4 rounded-full bg-bone/85 backdrop-blur px-3 py-1 text-xs tracking-wide text-olive-ink">
          {p.category}
        </div>
        {unavailable && (
          <div data-testid={`unavailable-${i}`}
               className="absolute top-3 right-3 rounded-full bg-forest-deep/85 text-bone text-xs px-3 py-1">
            Nicht verfügbar
          </div>
        )}
      </div>
      <div className="mt-5 flex items-end justify-between gap-4">
        <div>
          <h3 className="font-serif text-2xl text-ink">{p.title}</h3>
          <p className="mt-1 text-sm text-inkmuted max-w-sm leading-relaxed">{rich(p.description)}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-serif text-xl text-sage-deep">{p.price}</p>
          {unavailable ? (
            <span data-testid={`unavailable-label-${i}`}
                  className="mt-2 inline-block text-xs uppercase tracking-widest text-inkmuted/70">
              Nicht verfügbar
            </span>
          ) : (
            <button data-testid={`inquire-${i}`} onClick={() => onInquire(p)}
                    className="mt-2 text-xs uppercase tracking-widest text-clay hover:underline underline-offset-4">
              Anfragen
            </button>
          )}
        </div>
      </div>
    </Reveal>
  );
}

function Gallery({ products, onInquire }) {
  const c = useC();
  return (
    <section id="galerie" className="bg-page py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-sage mb-4">{c.gallery_overline}</p>
            <h2 className="font-serif text-ink text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02]">
              {c.gallery_heading1}<br /><span className="italic text-sage">{c.gallery_heading_accent}</span>
            </h2>
          </div>
          <p className="max-w-sm text-inkmuted leading-relaxed">
            {rich(c.gallery_intro)}
          </p>
        </Reveal>
        <div className="grid md:grid-cols-12 gap-8 md:gap-x-8 md:gap-y-16">
          {products.map((p, i) => <GalleryCard key={p.id || i} p={p} i={i} onInquire={onInquire} />)}
        </div>
      </div>
    </section>
  );
}

function Workshops({ workshops, onBook }) {
  const c = useC();
  return (
    <section id="workshops" className="relative bg-olive-ink text-bone py-28 md:py-40 overflow-hidden">
      <div className="grain absolute inset-0" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <Reveal className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.28em] text-sage mb-4">{c.workshops_overline}</p>
          <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.03]">
            {c.workshops_heading}
          </h2>
          <p className="mt-6 text-bone/60 leading-relaxed">
            {rich(c.workshops_intro)}
          </p>
        </Reveal>
        <div className="mt-16 divide-y divide-bone/15 border-t border-bone/15">
          {workshops.map((w, i) => (
            <Reveal key={w.id || i} delay={i * 0.06}>
              <div className="group flex items-center gap-6 py-8">
                {w.image_url && (
                  <img src={w.image_url} alt={w.title} style={{ objectPosition: w.image_pos || "50% 50%" }}
                       className="hidden sm:block h-24 w-32 shrink-0 rounded-2xl object-cover" />
                )}
                <div className="grid md:grid-cols-12 items-center gap-4 flex-1 min-w-0">
                  <p className="md:col-span-3 text-sm text-sage">{w.date}</p>
                  <div className="md:col-span-5">
                    <h3 className="font-serif text-2xl md:text-3xl">{w.title}</h3>
                    <p className="mt-1 text-sm text-bone/55 max-w-md">{rich(w.description)}</p>
                  </div>
                  <div className="md:col-span-2 text-sm text-bone/70">
                    <p>{w.location}</p>
                    <p className="mt-1">{w.spots} Plätze · {w.price}</p>
                  </div>
                  <div className="md:col-span-2 md:text-right">
                    <button data-testid={`book-${i}`} onClick={() => onBook(w)}
                            className="btn-lift inline-flex items-center gap-1.5 rounded-full bg-sage px-5 py-2.5 text-sm font-medium text-sage-deep hover:bg-bone transition-colors">
                      Jetzt verbindlich buchen <ArrowUpRight size={15} />
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Maerkte({ markets }) {
  const c = useC();
  if (!markets.length) return null;
  return (
    <section id="maerkte" className="bg-page py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <Reveal className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.28em] text-sage mb-4">{c.maerkte_overline}</p>
          <h2 className="font-serif text-ink text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.03]">
            {c.maerkte_heading}
          </h2>
          <p className="mt-6 text-inkmuted leading-relaxed">
            {rich(c.maerkte_intro)}
          </p>
        </Reveal>
        <div className="mt-16 divide-y divide-linetone border-t border-linetone">
          {markets.map((m, i) => (
            <Reveal key={m.id || i} delay={i * 0.06}>
              <div className="group grid md:grid-cols-12 items-center gap-4 py-8">
                <p className="md:col-span-3 text-sm text-sage-dark">{m.date}</p>
                <div className="md:col-span-6">
                  <h3 className="font-serif text-2xl md:text-3xl text-ink">{m.title}</h3>
                  <p className="mt-1 text-sm text-inkmuted max-w-md">{rich(m.description)}</p>
                </div>
                <div className="md:col-span-3 md:text-right">
                  <p className="text-sm text-inkmuted">{m.location}</p>
                  {m.url && (
                    <a href={m.url} target="_blank" rel="noopener noreferrer" data-testid={`market-link-${i}`}
                       className="mt-2 inline-flex items-center gap-1 text-xs uppercase tracking-widest text-clay hover:underline underline-offset-4">
                      Zum Markt <ArrowUpRight size={13} />
                    </a>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Contact({ prefill }) {
  const c = useC();
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, message: prefill }));
  }, [prefill]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/inquiries", { ...form, kind: "contact" });
      toast.success(data.message);
      setForm({ name: "", email: "", message: "", website: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="bg-panel2 py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid md:grid-cols-2 gap-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.28em] text-sage mb-4">{c.contact_overline}</p>
          <h2 className="font-serif text-ink text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02]">
            {c.contact_heading}<br /><span className="italic text-sage">{c.contact_accent}</span>
          </h2>
          <p className="mt-6 text-inkmuted leading-relaxed max-w-md">
            {rich(c.contact_intro)}
          </p>
          <div className="mt-10 space-y-4 text-ink">
            <a href={`mailto:${c.contact_email}`} className="flex items-center gap-3 hover:text-sage-dark transition-colors">
              <Mail size={18} className="text-sage" /> {c.contact_email}
            </a>
            <p className="flex items-center gap-3"><MapPin size={18} className="text-sage" /> {c.contact_location}</p>
            <a href="#" className="flex items-center gap-3 hover:text-sage-dark transition-colors">
              <Instagram size={18} className="text-sage" /> {c.contact_instagram}
            </a>
            {(c.tiktok_url || "").trim() ? (
              <a href={c.tiktok_url} target="_blank" rel="noopener noreferrer" data-testid="contact-tiktok"
                 className="flex items-center gap-3 hover:text-sage-dark transition-colors">
                <TikTokIcon size={18} className="text-sage" /> {c.contact_tiktok || "TikTok"}
              </a>
            ) : null}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
            <input type="text" name="website" value={form.website} tabIndex={-1} autoComplete="off"
                   aria-hidden="true" className="hidden"
                   onChange={(e) => setForm({ ...form, website: e.target.value })} />
            <div>
              <label className="text-xs uppercase tracking-widest text-inkmuted">Name</label>
              <Input required data-testid="contact-name" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     className="mt-2 bg-transparent border-0 border-b-2 border-linetone rounded-none px-0 focus-visible:ring-0 focus-visible:border-sage text-lg" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-inkmuted">E-Mail</label>
              <Input required type="email" data-testid="contact-email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="mt-2 bg-transparent border-0 border-b-2 border-linetone rounded-none px-0 focus-visible:ring-0 focus-visible:border-sage text-lg" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-inkmuted">Nachricht</label>
              <Textarea required rows={4} data-testid="contact-message" value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="mt-2 bg-transparent border-0 border-b-2 border-linetone rounded-none px-0 focus-visible:ring-0 focus-visible:border-sage text-lg resize-none" />
            </div>
            <Button type="submit" disabled={loading} data-testid="contact-submit"
                    className="btn-lift rounded-full bg-sage text-sage-deep hover:bg-sage-dark px-8 py-6 text-base">
              {loading ? "Wird gesendet…" : "Nachricht senden"}
            </Button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function Footer({ hasMarkets }) {
  const c = useC();
  const nav = buildNav(hasMarkets);
  return (
    <footer className="bg-olive-ink text-bone pt-24 pb-10 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid md:grid-cols-3 gap-10 border-b border-bone/15 pb-16">
          <div className="md:col-span-2 max-w-md">
            <div className="flex items-center gap-4 mb-6">
              <div className="h-28 w-28 rounded-full bg-bone flex items-center justify-center shrink-0">
                <img src={LOGO} alt="Tonpoesie" className="h-[6.875rem] w-[6.875rem] rounded-full object-cover" />
              </div>
              <span className="font-serif text-4xl">Tonpoesie</span>
            </div>
            <p className="font-serif text-2xl">{rich(c.footer_tagline)}</p>
          </div>
          <div className="flex md:justify-end gap-12 text-sm text-bone/60">
            <div className="space-y-2">
              {nav.map((n) => <a key={n.href} href={n.href} className="block hover:text-sage transition-colors">{n.label}</a>)}
            </div>
            <div className="space-y-2">
              <a href="/admin/login" data-testid="footer-admin-link" className="block hover:text-sage transition-colors">Admin</a>
              <a href={`mailto:${c.contact_email}`} className="block hover:text-sage transition-colors">E-Mail</a>
              {(c.tiktok_url || "").trim() ? (
                <a href={c.tiktok_url} target="_blank" rel="noopener noreferrer" data-testid="footer-tiktok-link"
                   className="flex items-center gap-1.5 hover:text-sage transition-colors">
                  <TikTokIcon size={14} /> {c.contact_tiktok || "TikTok"}
                </a>
              ) : null}
              <Link to="/impressum" data-testid="footer-impressum-link" className="block hover:text-sage transition-colors">Impressum</Link>
              <Link to="/datenschutz" data-testid="footer-datenschutz-link" className="block hover:text-sage transition-colors">Datenschutz</Link>
            </div>
          </div>
        </div>
        <div className="pt-10 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-bone/40">
          <span>© {new Date().getFullYear()} Tonpoesie Keramik Studio. Alle Unikate handgemacht.</span>
          <div className="flex items-center gap-4">
            <span>Merzig · Saarland</span>
          </div>
        </div>
        <h2 className="font-serif leading-none tracking-tighter text-[22vw] text-bone/8 mt-6 select-none">Tonpoesie</h2>
      </div>
    </footer>
  );
}

function BookingDialog({ workshop, onClose }) {
  const c = useC();
  const [form, setForm] = useState({ name: "", email: "", participants: 1, message: "", website: "" });
  const [participantNames, setParticipantNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agbOpen, setAgbOpen] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const open = !!workshop;
  const agbText = (c.agb_text || "").trim();
  const namesNeeded = Math.max(0, (Number(form.participants) || 1) - 1);
  const namesComplete = participantNames.slice(0, namesNeeded).every((n) => (n || "").trim() !== "");
  const canSubmit = agbAccepted && privacyAccepted && namesComplete;

  useEffect(() => {
    if (!open) {
      setAgbAccepted(false);
      setPrivacyAccepted(false);
      setParticipantNames([]);
    }
  }, [open]);

  // Vornamen-Felder wachsen/schrumpfen mit der Personenzahl (Person 1 = die buchende Person).
  useEffect(() => {
    setParticipantNames((prev) => {
      if (prev.length === namesNeeded) return prev;
      const next = prev.slice(0, namesNeeded);
      while (next.length < namesNeeded) next.push("");
      return next;
    });
  }, [namesNeeded]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Bitte fülle alle Pflichtfelder aus und bestätige AGB und Datenschutzerklärung.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form, participants: Number(form.participants),
        workshop_id: workshop.id, workshop_title: workshop.title,
        agb_accepted: true, privacy_accepted: true,
        participant_names: participantNames.slice(0, namesNeeded).map((n) => n.trim()),
      });
      toast.success(data.message);
      onClose();
      setForm({ name: "", email: "", participants: 1, message: "", website: "" });
      setParticipantNames([]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="bg-page border-linetone max-w-lg" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl text-ink">{workshop?.title}</DialogTitle>
            <DialogDescription className="text-inkmuted">{workshop?.date} · {workshop?.location} · {workshop?.price}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2" data-testid="booking-form">
            <input type="text" name="website" value={form.website} tabIndex={-1} autoComplete="off"
                   aria-hidden="true" className="hidden"
                   onChange={(e) => setForm({ ...form, website: e.target.value })} />
            <Input required placeholder="Dein Name" data-testid="booking-name" value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-panel2 border-linetone" />
            <Input required type="email" placeholder="E-Mail" data-testid="booking-email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-panel2 border-linetone" />
            <Input required type="number" min={1} max={10} placeholder="Anzahl Personen" data-testid="booking-participants"
                   value={form.participants} onChange={(e) => setForm({ ...form, participants: e.target.value })} className="bg-panel2 border-linetone" />
            {namesNeeded > 0 && (
              <div className="space-y-3">
                {Array.from({ length: namesNeeded }).map((_, i) => (
                  <Input key={i} required placeholder={`Vorname Person ${i + 2}`} data-testid={`booking-participant-name-${i}`}
                         value={participantNames[i] || ""}
                         onChange={(e) => setParticipantNames((prev) => {
                           const next = [...prev];
                           next[i] = e.target.value;
                           return next;
                         })}
                         className="bg-panel2 border-linetone" />
                ))}
              </div>
            )}
            <Textarea placeholder="Nachricht (optional)" rows={3} data-testid="booking-message" value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-panel2 border-linetone resize-none" />

            <div className="space-y-3 pt-1">
              <label className="flex items-start gap-2.5 text-sm text-inkmuted leading-snug">
                <Checkbox data-testid="agb-checkbox" checked={agbAccepted}
                          onCheckedChange={(v) => setAgbAccepted(!!v)}
                          className="mt-0.5 border-linetone data-[state=checked]:bg-sage data-[state=checked]:border-sage" />
                <span>
                  Ich habe die{" "}
                  {agbText ? (
                    <button type="button" data-testid="agb-link" onClick={() => setAgbOpen(true)}
                            className="underline underline-offset-2 hover:text-ink transition-colors">
                      AGB's
                    </button>
                  ) : "AGB's"}{" "}
                  gelesen und akzeptiere sie
                </span>
              </label>
              <label className="flex items-start gap-2.5 text-sm text-inkmuted leading-snug">
                <Checkbox data-testid="privacy-checkbox" checked={privacyAccepted}
                          onCheckedChange={(v) => setPrivacyAccepted(!!v)}
                          className="mt-0.5 border-linetone data-[state=checked]:bg-sage data-[state=checked]:border-sage" />
                <span>
                  Ich bin damit einverstanden, dass diese Daten zum Zweck der Kontaktaufnahme gespeichert
                  und verarbeitet werden. Mir ist bekannt, dass ich meine Einwilligung jederzeit widerrufen
                  kann.*
                </span>
              </label>
            </div>

            <Button type="submit" disabled={loading || !canSubmit} data-testid="booking-submit"
                    className="w-full rounded-full bg-sage text-sage-deep hover:bg-sage-dark py-6">
              {loading ? "Wird gesendet…" : "Verbindlich buchen"}
            </Button>
            {!canSubmit && (
              <p className="text-xs text-inkmuted text-center">
                Bitte fülle alle Pflichtfelder aus und bestätige AGB und Datenschutzerklärung, um fortzufahren.
              </p>
            )}
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={agbOpen} onOpenChange={setAgbOpen}>
        <DialogContent className="bg-page border-linetone max-w-lg" data-testid="agb-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">AGB</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto whitespace-pre-line text-sm text-inkmuted leading-relaxed">
            {richBlocks(agbText)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Showcase() {
  const c = useC();
  const imgs = c.showcase_images || [];
  if (!imgs.length) return null;
  const loop = [...imgs, ...imgs];
  return (
    <section className="bg-page py-24 md:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 md:px-10 mb-12">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.28em] text-sage mb-4">{c.showcase_overline}</p>
          <h2 className="font-serif text-ink text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02]">
            {c.showcase_heading}
          </h2>
        </Reveal>
      </div>
      <div className="space-y-5">
        <div className="flex w-max animate-marquee gap-5">
          {loop.map((src, i) => (
            <div key={`a${i}`} className="h-56 md:h-72 w-[16rem] md:w-[22rem] overflow-hidden rounded-2xl bg-panel2 shrink-0">
              <img src={src} alt="Keramik" loading="lazy" className="h-full w-full object-cover" />
            </div>
          ))}
        </div>
        <div className="flex w-max animate-marquee gap-5 [animation-direction:reverse]">
          {loop.map((src, i) => (
            <div key={`b${i}`} className="h-56 md:h-72 w-[16rem] md:w-[22rem] overflow-hidden rounded-2xl bg-panel2 shrink-0">
              <img src={src} alt="Keramik" loading="lazy" className="h-full w-full object-cover" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function InstaFeed() {
  const c = useC();
  const [liveItems, setLiveItems] = useState([]);
  const url = c.instagram_url || "https://instagram.com";

  useEffect(() => {
    api.get("/instagram").then((r) => {
      if (Array.isArray(r.data?.items) && r.data.items.length > 0) setLiveItems(r.data.items);
    }).catch(() => {});
  }, []);

  const usingLive = liveItems.length > 0;
  // Entweder die live via Instagram-API geladenen Beiträge oder die manuell gepflegten Bilder –
  // in beiden Fällen dieselbe Kachel-Darstellung (gleiche Klassen).
  const posts = usingLive
    ? liveItems.map((it) => ({ src: it.image, href: it.permalink || url, alt: it.caption || "Instagram-Beitrag" }))
    : (c.instagram_posts || []).map((src) => ({ src, href: url, alt: "Instagram Beitrag" }));

  return (
    <section className="bg-panel2 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-sage mb-4">{c.instagram_overline}</p>
            <h2 className="font-serif text-ink text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02]">
              {c.instagram_heading}
            </h2>
            <p className="mt-4 text-inkmuted leading-relaxed max-w-md">{rich(c.instagram_intro)}</p>
          </div>
          <a href={url} target="_blank" rel="noopener noreferrer" data-testid="instagram-follow"
             className="btn-lift inline-flex items-center gap-2 rounded-full bg-forest-deep px-6 py-3 text-sm font-medium text-bone hover:bg-sage-deep transition-colors shrink-0">
            <Instagram size={16} /> Auf Instagram folgen
          </a>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {posts.map((post, i) => (
            <a key={i} href={post.href} target="_blank" rel="noopener noreferrer" data-testid={`instagram-post-${i}`}
               className="group relative aspect-square overflow-hidden rounded-xl bg-page">
              <img src={post.src} alt={post.alt} loading="lazy"
                   className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <span className="absolute inset-0 flex items-center justify-center bg-forest-deep/0 group-hover:bg-forest-deep/45 transition-colors">
                <Instagram size={22} className="text-bone opacity-0 group-hover:opacity-100 transition-opacity" />
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Heutiges Datum als "YYYY-MM-DD" in lokaler Zeit (nicht UTC). */
function localTodayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function Landing() {
  const [products, setProducts] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [booking, setBooking] = useState(null);
  const [prefill, setPrefill] = useState("");
  const [content, setContent] = useState(DEFAULT_CONTENT);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
    const id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); };
  }, []);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/workshops").then((r) => setWorkshops(r.data)).catch(() => {});
    api.get("/markets").then((r) => setMarkets(r.data)).catch(() => {});
    api.get("/content").then((r) => {
      if (r.data && Object.keys(r.data).length) setContent({ ...DEFAULT_CONTENT, ...r.data });
    }).catch(() => {});
  }, []);

  // Vergangene Märkte (date_iso gesetzt und in der Vergangenheit) ausblenden, restliche nach
  // date_iso aufsteigend sortieren; Märkte ohne date_iso bleiben (in ursprünglicher Reihenfolge) hinten.
  const visibleMarkets = useMemo(() => {
    const today = localTodayIso();
    const withIndex = markets
      .filter((m) => !m.date_iso || m.date_iso >= today)
      .map((m, i) => ({ m, i }));
    withIndex.sort((a, b) => {
      if (!a.m.date_iso && !b.m.date_iso) return a.i - b.i;
      if (!a.m.date_iso) return 1;
      if (!b.m.date_iso) return -1;
      if (a.m.date_iso === b.m.date_iso) return a.i - b.i;
      return a.m.date_iso < b.m.date_iso ? -1 : 1;
    });
    return withIndex.map((x) => x.m);
  }, [markets]);

  const inquire = (p) => {
    setPrefill(`Ich interessiere mich für „${p.title}" (${p.price}). `);
    document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
    toast(`„${p.title}" ins Kontaktformular übernommen.`);
  };

  return (
    <ContentCtx.Provider value={content}>
      <div className="bg-page">
        <Header hasMarkets={visibleMarkets.length > 0} />
        <Hero />
        <Marquee />
        <About />
        <Manifesto />
        <Gallery products={products} onInquire={inquire} />
        <Showcase />
        <Workshops workshops={workshops} onBook={setBooking} />
        <Maerkte markets={visibleMarkets} />
        <InstaFeed />
        <Contact prefill={prefill} />
        <Footer hasMarkets={visibleMarkets.length > 0} />
        <BookingDialog workshop={booking} onClose={() => setBooking(null)} />
      </div>
    </ContentCtx.Provider>
  );
}
